import { Cause, Effect, Exit, Ref, Scope } from "effect";
import type { Context } from "effect";
import { INTERNAL_HTTP_MESSAGE } from "@workerstation/shared/errors/http";
import type { HistoryApiShape } from "../../api/history/service.ts";
import { historyFailureMessage } from "../../api/history/failure.ts";
import type { QueryWork } from "../../state/query-work.ts";
import type { HistoryState } from "./model.ts";

const closed = (scope: Scope.Scope): boolean => scope.state._tag === "Closed";

export const makeHistoryQueries = Effect.fn("Frontend.History.makeQueries")(function* (
  root: Scope.Scope, owner: Scope.Scope, state: Ref.Ref<HistoryState>, publish: Effect.Effect<void>,
  cleanupFailures: Ref.Ref<readonly Cause.Reason<never>[]>, api: HistoryApiShape, queries: Context.Service.Shape<typeof QueryWork>
) {
  const queryScope = yield* Ref.make(yield* Scope.fork(owner));
  const rememberCancellation = <A, E>(exit: Exit.Exit<A, E>) => {
    if (Exit.isFailure(exit) && exit.cause.reasons.some(reason => reason._tag === "Interrupt")) {
      const defects = exit.cause.reasons.filter(reason => reason._tag === "Die");
      return Ref.update(cleanupFailures, current => [...current, ...defects.filter(reason => !current.includes(reason))]);
    }
    return Effect.void;
  };
  const scopedQuery = <A, E, R>(operation: Effect.Effect<A, E, R>) => Effect.scoped(operation).pipe(Effect.onExit(rememberCancellation));
  const refresh = Effect.fn("Frontend.History.refresh")(function* (expectedEpoch?: number) {
    if (closed(root) || closed(owner)) return;
    const request = {};
    const selected = yield* Ref.modify(state, (current): readonly [HistoryState | null, HistoryState] => {
      if (current.closed || (expectedEpoch !== undefined && current.epoch !== expectedEpoch)) return [null, current] as const;
      const active = Boolean(current.selection?.stationId && current.selection.workspaceId && !current.selection.collapsed);
      return [current, { ...current, request, queryLoading: active, error: active ? null : current.error }] as const;
    });
    if (selected === null) return;
    yield* publish;
    const child = yield* Scope.fork(owner), previous = yield* Ref.getAndSet(queryScope, child);
    const cleanup = yield* Effect.exit(Scope.close(previous, Exit.void));
    if (Exit.isFailure(cleanup)) {
      yield* Ref.update(cleanupFailures, reasons => [...reasons, ...cleanup.cause.reasons]);
      return yield* cleanup;
    }
    const current = yield* Ref.get(state), selection = selected.selection;
    if (current.closed || current.request !== request || closed(root) || closed(owner) || !selection?.stationId || !selection.workspaceId || selection.collapsed) return;
    const owns = (value: HistoryState): boolean => !value.closed && !closed(root) && !closed(owner) && value.request === request && value.epoch === selected.epoch;
    const update = (change: (value: HistoryState) => HistoryState) => Ref.modify(state, value => {
      if (!owns(value)) return [false, value] as const;
      return [true, change(value)] as const;
    }).pipe(Effect.flatMap(changed => changed ? publish : Effect.void));
    const failed = (message: string) => update(value => ({ ...value, loaded: true, error: message }));
    const load = Effect.all({
      tags: scopedQuery(api.tags(selection.stationId)), revisions: scopedQuery(api.revisions(selection.workspaceId)),
      stationUpdatedAt: scopedQuery(api.stationUpdatedAt(selection.stationId)), workspaceUpdatedAt: scopedQuery(api.workspaceUpdatedAt(selection.workspaceId))
    }, { concurrency: "unbounded" }).pipe(Effect.map(value => ({ tags: value.tags.tags, revisions: value.revisions.revisions, stationUpdatedAt: value.stationUpdatedAt, workspaceUpdatedAt: value.workspaceUpdatedAt })));
    const job = queries.observe(load).pipe(
      Effect.onExit(rememberCancellation),
      Effect.matchEffect({ onFailure: error => failed(historyFailureMessage(error)), onSuccess: data => update(value => ({ ...value, loaded: true, data, error: null })) }),
      Effect.catchCause(() => failed(INTERNAL_HTTP_MESSAGE)),
      Effect.ensuring(update(value => ({ ...value, request: null, queryLoading: false })))
    );
    yield* Effect.forkIn(job, child, { uninterruptible: false });
  });
  return { refresh, rememberCancellation, scopedQuery };
});
